import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  ModalTitle,
  ModalContent,
  ModalActions,
  Button,
  TextInput,
  Label,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Checkbox,
} from '@c2fo/liquidity';
import type { User } from '../types';
import { INDUSTRY_OPTIONS, ROLE_OPTIONS, type SelectOption } from './EditUserModal.constants';
import { FISCAL_YEAR_END_OPTIONS } from '../../../constants/user';
import { getCountryOptions, type CountryOption } from '../utils/countryOptions';
import { useLocales, localesToSelectOptions } from '../../../hooks/useLocales';

interface EditUserModalProps {
  open: boolean;
  user: User | null;
  onClose: () => void;
  onSave: (userData: Partial<User>) => Promise<void>;
  onForgetUser: () => Promise<void>;
  saving: boolean;
  forgettingUser: boolean;
}

export const EditUserModal: React.FC<EditUserModalProps> = ({
  open,
  user,
  onClose,
  onSave,
  onForgetUser,
  saving,
  forgettingUser,
}) => {
  const [formData, setFormData] = useState<Partial<User>>({});
  const [selectedFiscalYearEnd, setSelectedFiscalYearEnd] = useState<SelectOption | null>(null);
  const [selectedIndustry, setSelectedIndustry] = useState<SelectOption | null>(null);
  const [selectedRole, setSelectedRole] = useState<SelectOption | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<CountryOption | null>(null);
  const [selectedLocale, setSelectedLocale] = useState<SelectOption | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [showForgetConfirmation, setShowForgetConfirmation] = useState(false);

  // Generate country options once
  const countryOptions = useMemo(() => getCountryOptions(), []);

  // Fetch locale options from API
  const { data: locales, isLoading: localesLoading } = useLocales();
  const localeOptions = useMemo(() => (locales ? localesToSelectOptions(locales) : []), [locales]);

  // Initialize form data when user changes or modal opens
  useEffect(() => {
    if (user && open) {
      setFormData({
        firstName: user.firstName ?? '',
        lastName: user.lastName ?? '',
        title: user.title ?? '',
        primaryPhoneNumber: user.primaryPhoneNumber ?? '',
        secondaryPhoneNumber: user.secondaryPhoneNumber ?? '',
        arDepartmentEmailAddress: user.arDepartmentEmailAddress ?? '',
        postalCode: user.postalCode ?? '',
        country: user.country ?? '',
        language: user.language ?? '',
        role: user.role ?? '',
        industry: user.industry ?? '',
        fiscalYearEnd: user.fiscalYearEnd ?? '',
        supplierReadOnly: user.supplierReadOnly ?? false,
      });
      // Clear validation error when modal opens with valid data
      setValidationError(null);

      // Set dropdown selections
      const fiscalYearEnd = user.fiscalYearEnd
        ? (FISCAL_YEAR_END_OPTIONS.find((opt) => opt.value === user.fiscalYearEnd) ?? null)
        : null;
      setSelectedFiscalYearEnd(fiscalYearEnd);

      const industry = user.industry ? (INDUSTRY_OPTIONS.find((opt) => opt.value === user.industry) ?? null) : null;
      setSelectedIndustry(industry);

      const role = user.role ? (ROLE_OPTIONS.find((opt) => opt.value === user.role) ?? null) : null;
      setSelectedRole(role);

      const country = user.country ? (countryOptions.find((opt) => opt.value === user.country) ?? null) : null;
      setSelectedCountry(country);

      const locale = user.language ? (localeOptions.find((opt) => opt.value === user.language) ?? null) : null;
      setSelectedLocale(locale);
    } else if (!open) {
      // Clear validation error when modal closes
      setValidationError(null);
    }
  }, [user, open, countryOptions, localeOptions]);

  const validateForm = (): boolean => {
    const firstNameTrimmed = (formData.firstName ?? '').trim();
    const lastNameTrimmed = (formData.lastName || '').trim();

    if (!firstNameTrimmed && !lastNameTrimmed) {
      setValidationError('First Name and Last Name are required');
      return false;
    }

    if (!firstNameTrimmed) {
      setValidationError('First Name is required');
      return false;
    }

    if (!lastNameTrimmed) {
      setValidationError('Last Name is required');
      return false;
    }

    setValidationError(null);
    return true;
  };

  const handleSubmit = async () => {
    if (!user) return;

    if (!validateForm()) {
      return;
    }

    const firstNameTrimmed = (formData.firstName ?? '').trim();
    const lastNameTrimmed = (formData.lastName || '').trim();

    const userData: Partial<User> = {
      ...formData,
      firstName: firstNameTrimmed || undefined,
      lastName: lastNameTrimmed || undefined,
      title: (formData.title ?? '').trim() || null,
      arDepartmentEmailAddress: (formData.arDepartmentEmailAddress ?? '').trim() || null,
      fiscalYearEnd: selectedFiscalYearEnd?.value ?? null,
      industry: selectedIndustry?.value ?? null,
      role: selectedRole?.value ?? null,
      country: selectedCountry?.value ?? null,
      language: selectedLocale?.value ?? null,
    };

    try {
      await onSave(userData);
      setValidationError(null);
      onClose();
    } catch (err) {
      // Error handled in parent
      throw err;
    }
  };

  // Early return if no user - but ensure component stays mounted during prop changes
  if (!user) return null;

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await handleSubmit();
  };

  const isFormValid = (() => {
    const firstNameTrimmed = (formData.firstName ?? '').trim();
    const lastNameTrimmed = (formData.lastName || '').trim();
    return firstNameTrimmed && lastNameTrimmed;
  })();

  return (
    <>
      <Modal open={open} onClose={onClose} size="auto" className="overflow-y-auto">
        <ModalTitle>Edit User</ModalTitle>
        <form onSubmit={handleFormSubmit}>
          <ModalContent>
            {validationError && (
              <div className="mb-4 p-2 bg-red-50 border border-red-200 rounded text-red-600 text-sm" role="alert">
                {validationError}
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="firstName" className="block mb-2">
                  First Name
                </Label>
                <TextInput
                  id="firstName"
                  value={formData.firstName ?? ''}
                  onChange={(e) => {
                    const newFirstName = e.target.value;
                    const newLastName = formData.lastName || '';
                    setFormData({ ...formData, firstName: newFirstName });
                    // Show validation error based on what's missing
                    const firstNameTrimmed = newFirstName.trim();
                    const lastNameTrimmed = newLastName.trim();
                    if (!firstNameTrimmed && !lastNameTrimmed) {
                      setValidationError('First Name and Last Name are required');
                    } else if (!firstNameTrimmed) {
                      setValidationError('First Name is required');
                    } else if (!lastNameTrimmed) {
                      setValidationError('Last Name is required');
                    } else {
                      setValidationError(null);
                    }
                  }}
                  className="w-full"
                />
              </div>

              <div>
                <Label htmlFor="lastName" className="block mb-2">
                  Last Name
                </Label>
                <TextInput
                  id="lastName"
                  value={formData.lastName || ''}
                  onChange={(e) => {
                    const newLastName = e.target.value;
                    const newFirstName = formData.firstName ?? '';
                    setFormData({ ...formData, lastName: newLastName });
                    // Show validation error based on what's missing
                    const firstNameTrimmed = newFirstName.trim();
                    const lastNameTrimmed = newLastName.trim();
                    if (!firstNameTrimmed && !lastNameTrimmed) {
                      setValidationError('First Name and Last Name are required');
                    } else if (!firstNameTrimmed) {
                      setValidationError('First Name is required');
                    } else if (!lastNameTrimmed) {
                      setValidationError('Last Name is required');
                    } else {
                      setValidationError(null);
                    }
                  }}
                  className="w-full"
                />
              </div>

              <div>
                <Label htmlFor="role" className="block mb-2">
                  Role
                </Label>
                <Select
                  value={selectedRole?.value ?? ''}
                  onValueChange={(value) => {
                    const role = ROLE_OPTIONS.find((opt) => opt.value === value);
                    setSelectedRole(role ?? null);
                  }}
                >
                  <SelectTrigger id="role" className="w-full">
                    <SelectValue placeholder="Select Role" />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLE_OPTIONS.map((role) => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="title" className="block mb-2">
                  Title
                </Label>
                <TextInput
                  id="title"
                  value={formData.title || ''}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full"
                />
              </div>

              <div>
                <Label htmlFor="primaryPhone" className="block mb-2">
                  Primary Phone
                </Label>
                <TextInput
                  id="primaryPhone"
                  value={formData.primaryPhoneNumber || ''}
                  onChange={(e) => setFormData({ ...formData, primaryPhoneNumber: e.target.value })}
                  className="w-full"
                />
              </div>

              <div>
                <Label htmlFor="secondaryPhone" className="block mb-2">
                  Secondary Phone
                </Label>
                <TextInput
                  id="secondaryPhone"
                  value={formData.secondaryPhoneNumber || ''}
                  onChange={(e) => setFormData({ ...formData, secondaryPhoneNumber: e.target.value })}
                  className="w-full"
                />
              </div>

              <div>
                <Label htmlFor="arDepartmentEmail" className="block mb-2">
                  AR Department Email
                </Label>
                <TextInput
                  id="arDepartmentEmail"
                  type="email"
                  value={formData.arDepartmentEmailAddress || ''}
                  onChange={(e) => setFormData({ ...formData, arDepartmentEmailAddress: e.target.value })}
                  className="w-full"
                />
              </div>

              <div>
                <Label htmlFor="fiscalYearEnd" className="block mb-2">
                  Fiscal Year End
                </Label>
                <Select
                  value={selectedFiscalYearEnd?.value ?? ''}
                  onValueChange={(value) => {
                    const fiscalYearEnd = FISCAL_YEAR_END_OPTIONS.find((opt) => opt.value === value);
                    setSelectedFiscalYearEnd(fiscalYearEnd ?? null);
                  }}
                >
                  <SelectTrigger id="fiscalYearEnd" className="w-full">
                    <SelectValue placeholder="Select Fiscal Year End" />
                  </SelectTrigger>
                  <SelectContent>
                    {FISCAL_YEAR_END_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="industry" className="block mb-2">
                  Industry
                </Label>
                <Select
                  value={selectedIndustry?.value ?? ''}
                  onValueChange={(value) => {
                    const industry = INDUSTRY_OPTIONS.find((opt) => opt.value === value);
                    setSelectedIndustry(industry ?? null);
                  }}
                >
                  <SelectTrigger id="industry" className="w-full">
                    <SelectValue placeholder="Select Industry" />
                  </SelectTrigger>
                  <SelectContent>
                    {INDUSTRY_OPTIONS.map((industry) => (
                      <SelectItem key={industry.value} value={industry.value}>
                        {industry.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="postalCode" className="block mb-2">
                  Postal Code
                </Label>
                <TextInput
                  id="postalCode"
                  value={formData.postalCode || ''}
                  onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                  className="w-full"
                />
              </div>

              <div>
                <Label htmlFor="country" className="block mb-2">
                  Country
                </Label>
                <Select
                  value={selectedCountry?.value ?? ''}
                  onValueChange={(value) => {
                    const country = countryOptions.find((opt) => opt.value === value);
                    setSelectedCountry(country ?? null);
                  }}
                >
                  <SelectTrigger id="country" className="w-full">
                    <SelectValue placeholder="Select Country" />
                  </SelectTrigger>
                  <SelectContent>
                    {countryOptions.map((country) => (
                      <SelectItem key={country.value} value={country.value}>
                        {country.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="language" className="block mb-2">
                  Language
                </Label>
                <Select
                  value={selectedLocale?.value ?? ''}
                  onValueChange={(value) => {
                    const locale = localeOptions.find((opt) => opt.value === value);
                    setSelectedLocale(locale ?? null);
                  }}
                  disabled={localesLoading}
                >
                  <SelectTrigger id="language" className="w-full">
                    <SelectValue placeholder={localesLoading ? 'Loading...' : 'Select Language'} />
                  </SelectTrigger>
                  <SelectContent>
                    {localeOptions.map((locale) => (
                      <SelectItem key={locale.value} value={locale.value}>
                        {locale.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="col-span-2">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="supplierReadOnly"
                    checked={formData.supplierReadOnly || false}
                    onChange={(e) => setFormData({ ...formData, supplierReadOnly: e.target.checked })}
                  />
                  <Label htmlFor="supplierReadOnly" className="mb-0 cursor-pointer">
                    Supplier Read-Only User
                  </Label>
                </div>
              </div>
            </div>
          </ModalContent>
          <ModalActions>
            <div className="flex justify-between items-center w-full">
              <Button
                type="button"
                variant="destructive"
                onClick={() => setShowForgetConfirmation(true)}
                disabled={saving || forgettingUser}
              >
                Forget User
              </Button>
              <div className="flex gap-2 items-center">
                <Button type="button" variant="secondary" onClick={onClose} disabled={saving || forgettingUser}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  loading={saving}
                  disabled={saving || forgettingUser || !isFormValid}
                >
                  Save
                </Button>
              </div>
            </div>
          </ModalActions>
        </form>
      </Modal>

      {/* Forget User Confirmation Modal */}
      <Modal open={showForgetConfirmation} onClose={() => setShowForgetConfirmation(false)} size="sm">
        <ModalTitle>fss-admin.c2fo.com says</ModalTitle>
        <ModalContent>
          <div className="space-y-4">
            <p>You are forgetting this user:</p>
            <p className="font-semibold">{user.emailAddress}</p>
            <p>
              By doing this, {user.emailAddress} is exercising their right to remove all personal data from C2FO and
              stopping all C2FO messaging.
            </p>
            <p>Are you sure you want to continue?</p>
          </div>
        </ModalContent>
        <ModalActions>
          <Button
            type="button"
            variant="secondary"
            onClick={() => setShowForgetConfirmation(false)}
            disabled={forgettingUser}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={async () => {
              try {
                await onForgetUser();
                setShowForgetConfirmation(false);
                onClose();
              } catch (err) {
                // Error handling is done in useUserActions hook
              }
            }}
            loading={forgettingUser}
          >
            OK
          </Button>
        </ModalActions>
      </Modal>
    </>
  );
};
